/**
 * Tenants Store
 *
 * Zustand store backing the Tenant-details tab: it owns the fetch of a single
 * tenant's details (`GET /api/tenant-management/orgs/{id}`) and the delete action
 * (`DELETE …`). Keeping the async work here (rather than in a component effect)
 * matches the users/orgs stores and keeps effects free of direct component
 * setState.
 *
 * Uses the TenantsApiClient from the registry.
 *
 * @module stores/useTenantsStore
 */

import { create, type StateCreator } from 'zustand';
import type { TenantDetails, UpdateTenantRequest } from '../types';
import type { TenantsApiClient } from '../api/TenantsApiClient';
import { tenantsClient as defaultTenantsClient } from '../api/registry';

// =============================================================================
// Types
// =============================================================================

export interface TenantsState {
  /** The currently-loaded tenant's details (may be for a previous org until a
   *  new load resolves — consumers should check `details.id` against their org). */
  details: TenantDetails | null;
  isLoading: boolean;
  error: Error | null;

  /** Fetch details for orgId. Sets isLoading, then details or error. */
  loadTenant: (orgId: string) => Promise<void>;
  /** Partially update a tenant's editable fields, then refetch its details so
   *  the panel reflects the change. Rejects on failure (the edit dialog shows
   *  the error inline); a successful update always resolves after the refetch
   *  starts the panel refresh. */
  updateTenant: (orgId: string, req: UpdateTenantRequest) => Promise<void>;
  /** Delete a tenant. Resolves on success; rejects on failure (caller shows the
   *  error). Does not touch the picker list — the page reloads it. */
  deleteTenant: (orgId: string) => Promise<void>;
  /** Clear all state. */
  reset: () => void;
}

// =============================================================================
// Factory
// =============================================================================

export function createTenantsStoreCreator(
  tenantsClient: TenantsApiClient = defaultTenantsClient
): StateCreator<TenantsState> {
  return (set, get) => ({
    details: null,
    isLoading: false,
    error: null,

    loadTenant: async (orgId: string) => {
      set({ isLoading: true, error: null });
      try {
        const details = await tenantsClient.getTenant(orgId);
        set({ details, isLoading: false });
      } catch (error) {
        set({
          error: error instanceof Error ? error : new Error(String(error)),
          details: null,
          isLoading: false,
        });
      }
    },

    updateTenant: async (orgId: string, req: UpdateTenantRequest) => {
      // Let the caller (the edit dialog) catch and display failures; only a
      // successful update refetches, so the panel never flashes stale data
      // after an error.
      await tenantsClient.updateTenant(orgId, req);
      await get().loadTenant(orgId);
    },

    deleteTenant: async (orgId: string) => {
      // Let the caller (the confirm dialog) catch and display failures.
      await tenantsClient.deleteTenant(orgId);
    },

    reset: () => set({ details: null, isLoading: false, error: null }),
  });
}

// =============================================================================
// Store Factory
// =============================================================================

export function createTenantsStore(tenantsClient?: TenantsApiClient) {
  return create<TenantsState>(createTenantsStoreCreator(tenantsClient));
}

// =============================================================================
// Default Export
// =============================================================================

export const useTenantsStore = createTenantsStore();
